import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Field, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { CatalogHeader, errorText } from "./common";

export function ProfilesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const data = useLoad((a) => a.admin.catalog.profiles());
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function startDraft(profileId: string, fromVersionId: string | null) {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.admin.catalog.createProfileDraft(profileId, fromVersionId);
      navigate(`/catalog/profiles/versions/${res.draft.id}`);
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const f = await api.admin.catalog.createProfile({ code: code.trim(), name: name.trim() });
      setMsg({ kind: "info", text: `Created ${f.code}. Start a draft to enter its prevalence, severity and seasons.` });
      setCode("");
      setName("");
      data.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <CatalogHeader title="Allergen profiles" intro="Prevalence, severity and seasons by region, read by the testing algorithm. Versioned like panels; independent of the tray." />
      {data.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load profiles (${errorText(data.error)})`} /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <Table caption={data.data ? `${data.data.families.length} profiles` : data.state === "error" ? "Not loaded" : "Loading…"}>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Published</Th>
                <Th>Draft</Th>
              </tr>
            </thead>
            <tbody>
              {data.data?.families.map((f) => (
                <tr key={f.id}>
                  <Td className="font-medium">{f.code}</Td>
                  <Td>{f.name}</Td>
                  <Td>{f.published ? <Link to={`/catalog/profiles/versions/${f.published.id}`} className="text-anchor underline">v{f.published.version}</Link> : <span className="text-ink-secondary">none</span>}</Td>
                  <Td>
                    {f.draft ? (
                      <Link to={`/catalog/profiles/versions/${f.draft.id}`} className="text-anchor underline">v{f.draft.version} draft</Link>
                    ) : (
                      <Button variant="secondary" surface="card" disabled={busy} onClick={() => void startDraft(f.id, f.published?.id ?? null)}>Start draft</Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {data.data?.families.length === 0 && <p className="mt-3 text-ink-secondary">No profiles yet. Create the first one.</p>}
        </Card>
        <Card as="section">
          <h2 className="mb-4 text-h3 font-semibold text-anchor">New profile</h2>
          <form onSubmit={(e) => void create(e)} className="grid gap-4">
            <Field label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required hint="2–12 letters, digits or hyphens, e.g. FL-S" />
            <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            <div><Button type="submit" disabled={busy}>Create profile</Button></div>
          </form>
        </Card>
      </div>
    </>
  );
}
